/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useIntl} from 'react-intl'
import {
    Flex,
    useMultiStyleConfig,
    Image,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCustomPreferenceWithLocale} from '../../../hooks/use-custom-preference'
import PropTypes from 'prop-types'

//ICONS
import {
    AmericanexpressIcon,
    ApplepayIcon,
    KlarnaIcon,
    MastercardIcon,
    PaypalIcon,
    TwintIcon,
    VisaIcon,
} from '../../../components/icons'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const paymentIcons = [
    {
        key: 'applepay',
        icon: ApplepayIcon,
        label: {
            id: 'payment_methods.applepay.label',
            defaultMessage: 'Apple Pay payment method',
        },
    },
    {
        key: 'amex',
        icon: AmericanexpressIcon,
        label: {
            id: 'payment_methods.amex.label',
            defaultMessage: 'American Express payment method',
        },
    },
    {
        key: 'mastercard',
        icon: MastercardIcon,
        label: {
            id: 'payment_methods.mastercard.label',
            defaultMessage: 'Mastercard payment method',
        },
    },
    {
        key: 'paypal',
        icon: PaypalIcon,
        label: {
            id: 'payment_methods.paypal.label',
            defaultMessage: 'PayPal payment method',
        },
    },
    {
        key: 'klarna',
        icon: KlarnaIcon,
        label: {
            id: 'payment_methods.klarna.label',
            defaultMessage: 'Klarna payment method',
        },
    },
    {
        key: 'visa',
        icon: VisaIcon,
        label: {
            id: 'payment_methods.visa.label',
            defaultMessage: 'Visa payment method',
        },
    },
    {
        key: 'twint',
        icon: TwintIcon,
        label: {
            id: 'payment_methods.twint.label',
            defaultMessage: 'Twint payment method',
        },
    },
]

const subscriptionPaymentKeys = ['mastercard', 'visa', 'amex']

const CartCta = ({variant = 'default'} = {}) => {
    const style = useMultiStyleConfig('Cart', {variant})
    const {formatMessage} = useIntl()
    const {paymentMethods} = style
    const {data: basket} = useCurrentBasket()
    const {locale} = useMultiSite()

    const paymentLinksForLocale = useCustomPreferenceWithLocale('paymentLogosOrderSummary')

    const hasSubscription = basket?.productItems?.some((item) => item?.c_isSubscription)

    let iconsToShow

    if (hasSubscription) {
        // If there is a subscription, always use the subscription payment keys.
        iconsToShow = paymentIcons.filter((p) => subscriptionPaymentKeys.includes(p.key))
    } else if (paymentLinksForLocale) {
        // If no subscription and we have links from the custom preference, use them.
        iconsToShow = Object.entries(paymentLinksForLocale).map(([key, src]) => ({
            key,
            src,
        }))
    } else {
        // Otherwise, use the fallback logic with local icons (excluding subscription check).
        iconsToShow = paymentIcons.filter((p) => {
            if (p.key === 'twint') {
                return locale.alias === 'fr-ch' || locale.alias === 'de-ch'
            }
            return true
        })
    }

    return (
        <Flex {...paymentMethods?.iconsContainer}>
            {/* Conditionally render based on whether we have custom links or are using the fallback */}
            {paymentLinksForLocale && !hasSubscription
                ? iconsToShow.map(({key, src}) => (
                      <Flex key={key} {...paymentMethods?.iconContainer}>
                          <Image src={src} alt={key} {...paymentMethods?.icon} />
                      </Flex>
                  ))
                : iconsToShow.map(({key, icon: Icon, label}) => (
                      <Flex
                          key={key}
                          {...paymentMethods?.iconContainer}
                          aria-label={formatMessage(label)}
                      >
                          <Icon {...paymentMethods?.icon} />
                      </Flex>
                  ))}
            {/*TODO : MISSING AFFIRM AND AFTER PAY */}
        </Flex>
    )
}

export default CartCta

CartCta.propTypes = {
    variant: PropTypes.string,
}
